import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EmailCampaignStatusHTMLSpanDraw, EmailHistoryStatus } from '@models/email/email-issue';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { EmailHistoryService } from '@services/email-history.service';
import { LoaderService } from '@services/loader.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { formateDateTime } from '@utils/utils';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-email-history-all',
  templateUrl: './email-history-all.component.html',
  styleUrls: ['./email-history-all.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class EmailListAllComponent implements OnInit, AfterViewInit {
  @ViewChild('grid') grid: AtlasGridComponent;
  @ViewChild('modalEmailPreview') modalEmailPreview;
  @ViewChild('modalConfirmDelete') modalConfirmDelete;
  modalEmailPreviewModal: NgbModalRef;
  modalConfirmDeleteModal: NgbModalRef;

  selectedEmailPreview = '';
  isLoaderActive = false;

  tableColumnsDefs;
  tableGridOptions;
  tableLoading: boolean;
  tableRowData = null;
  paginationPageSize = 10;
  showGrid = false;
  gridAnimationState = 'hide';

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  constructor(
    private emailHistoryService: EmailHistoryService,
    private translateService: TranslateService,
    private loaderService: LoaderService,
    private atlasToast: AtlasToastService,
    private modalService: NgbModal,
    private router: Router
  ) {}

  ngAfterViewInit(): void {
    this.grid.dataIsLoaded.subscribe(() => {
      this.gridAnimationState = 'show';
    });
  }

  ngOnInit(): void {
    this.defineTable();

    this.gridOptionDefinition();
  }

  defineTable() {
    this.tableColumnsDefs = [
      {
        headerNameTranslate: 'email_campaign_info',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              return this.campaignEmailHtml(params.data);
            }
            return '';
          }
        },
        sortable: false,
        onCellClicked: params => {
          this.openPreviewModal(params.data.idCampaignIssue);
        },
        cellStyle: { 'line-height': '25px' },
        width: 200
      },
      {
        headerNameTranslate: 'email_campaign_status',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            return params && params.data ? this.getHtmlRenderEmailCampaignStatus(params.data) : '';
          }
        },
        suppressCellFocus: true,
        sortable: false,
        width: 120
      },
      {
        headerNameTranslate: 'email_campaign_recipients',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (!params || !params.data) {
              return '';
            }
            if (params.data.sendStatus === 'DRAFT') {
              return '';
            }
            if (params.data.countRecipients) {
              return '<div class="campaign-email-recipients">' + params.data.countRecipients + '</div>';
            }
            return '';
          }
        },
        width: 100,
        suppressCellFocus: true,
        sortable: false
      },
      {
        headerNameTranslate: 'email_campaign_percentage',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (!params || !params.data) {
              return '';
            }
            if (params.data.sendStatus === 'DRAFT') {
              return '';
            }
            if (params.data.countRecipients) {
              return '<div><span class="campaign-email-opens">' + params.data.uniqueOpens + ' %</span></div>';
            }
            return '';
          }
        },
        suppressCellFocus: true,
        width: 100,
        cellClass: ['campaign-email-opens'],
        sortable: false
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              onClick: e => this.router.navigate(['/mail/send'], { queryParams: { copy: e.idCampaignIssue } }),
              label: 'common_copy'
            },
            {
              onClick: e => this.openPreviewModal(e.idCampaignIssue),
              label: 'mail_preview'
            },
            {
              onClick: e => this.router.navigate(['/mail/stats', e.idCampaignIssue]),
              label: 'common_stat',
              hide: e => e?.sendStatus === EmailHistoryStatus.DRAFT
            },
            {
              onClick: e => this.router.navigate(['/mail/send'], { queryParams: { edit: e.idCampaignIssue } }),
              label: 'common_edit',
              hide: e => e?.sendStatus !== EmailHistoryStatus.DRAFT
            },
            {
              onClick: e => this.openConfirmDeleteModal(e.idCampaignIssue),
              label: 'common_delete',
              hide: e => (e?.sendStatus ? this.hideDeleteButton(e) : true)
            }
          ]
        },

        cellClass: ['button-group', 'text-end'],
        maxWidth: 82,
        field: '__actions'
      }
    ];
  }

  hideDeleteButton(params) {
    switch (params.sendStatus) {
      case EmailHistoryStatus.DRAFT:
        return false;

        break;
      case EmailHistoryStatus.SCHEDULED:
        return false;
        break;
      case EmailHistoryStatus.SENT:
        return true;

        break;
      case EmailHistoryStatus.ERROR:
        return true;
        break;

      default:
        return true;
        break;
    }
  }

  gridOptionDefinition() {
    const that = this;
    this.tableGridOptions = AtlasGridOptions.deserialize({
      rowHeight: 110,
      pagination: true,
      // resizable: false,
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadList.bind(that),
      noRowsOverlayOptions: {
        message: ''
      }
      // onFirstDataRendered: () => {
      //   setTimeout(() => {
      //     this.gridAnimationState = 'show';
      //   }, 150);
      // }
    });
    this.reloadList();
  }

  reloadList(startRow?, endRow?, sort?) {
    if (!this.isLoaderActive) {
      this.loaderService.show();
      this.isLoaderActive = true;
    }
    return this.emailHistoryService
      .getSmsSentList(
        null,
        this.getExcludeStatus(),
        null,
        null,
        null,
        null,
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        'lastSaveTime',
        'd'
      )
      .pipe(
        finalize(() => {
          this.loaderService.hide();
          this.isLoaderActive = false;
        })
      );
  }

  getExcludeStatus() {
    return [EmailHistoryStatus.ARCHIVED, EmailHistoryStatus.BLOCKED];
  }

  campaignEmailHtml(campaign) {
    return (
      '<div class="campaign-email-title">' +
      campaign.name +
      '</div>' +
      '<div class="campaign-email-subject">' +
      campaign.subject +
      '</div>' +
      this.getEditedOrSentHtml(campaign) +
      '</div>'
    );
  }

  getEditedOrSentHtml(emailCampaign) {
    const labels = [
      'mail_list_drafts_table_savedon',
      'smsadv_list_table_scheduled_on',
      'mail_list_sent_table_senton',
      'mail_send_campaign_input'
    ];
    const edited = this.translateService.instant(labels[0]);
    const scheduled = this.translateService.instant(labels[1]);
    const sentOn = this.translateService.instant(labels[2]);
    const list = this.translateService.instant(labels[3]);
    if (emailCampaign.sendStatus === EmailHistoryStatus.DRAFT) {
      return (
        '<div class="campaign-email-editDate">' +
        '<span class="sendDate">' +
        edited +
        ': </span>' +
        (emailCampaign?.sendDate ? this.getFormattedDateHtml(emailCampaign.sendDate) : '') +
        '<span class="lastSaveTime"> ' +
        list +
        ': </span>' +
        (emailCampaign?.campaign?.title ? emailCampaign.campaign.title : '') +
        '</div>'
      );
    } else if (emailCampaign.sendStatus === EmailHistoryStatus.SCHEDULED) {
      return (
        '<div class="campaign-email-editDate">' +
        '<span class="sendDate">' +
        scheduled +
        ': </span>' +
        (emailCampaign?.sendDate ? this.getFormattedDateHtml(emailCampaign.sendDate) : '') +
        '<span class="lastSaveTime"> ' +
        list +
        ': </span>' +
        (emailCampaign?.campaign?.title ? emailCampaign.campaign.title : '') +
        '</div>'
      );
    } else if (emailCampaign.sendDate) {
      return (
        '<div class="campaign-email-editDate">' +
        '<span class="sendDate">' +
        sentOn +
        ': </span>' +
        (emailCampaign?.sendDate ? this.getFormattedDateHtml(emailCampaign.sendDate) : '') +
        '<span class="lastSaveTime"> ' +
        list +
        ': </span>' +
        (emailCampaign?.campaign?.title ? emailCampaign.campaign.title : '') +
        '</div>'
      );
    } else {
      return (
        '<div class="campaign-email-editDate">' +
        '<span class="sendDate">' +
        sentOn +
        ': </span>' +
        (emailCampaign?.lastSaveTime ? this.getFormattedDateHtml(emailCampaign.lastSaveTime) : ' -- ') +
        '<span class="lastSaveTime"> ' +
        list +
        ': </span>' +
        (emailCampaign?.campaign?.title ? emailCampaign.campaign.title : '') +
        '</div>'
      );
    }
  }

  private getHtmlRenderEmailCampaignStatus(emailCampaign) {
    const status = emailCampaign.sendStatus;
    switch (status) {
      case EmailHistoryStatus.DRAFT:
        return this.getHtmlRenderStatus('mail_send_status_0', EmailCampaignStatusHTMLSpanDraw.DRAFT);
      case EmailHistoryStatus.RUNNING:
        return this.getHtmlRenderStatus('mail_send_status_1', EmailCampaignStatusHTMLSpanDraw.SENT);
      case EmailHistoryStatus.SENT:
        return this.getHtmlRenderStatus('mail_send_status_2', EmailCampaignStatusHTMLSpanDraw.SENT);
      case EmailHistoryStatus.SCHEDULED:
        return this.getHtmlRenderStatus('mail_send_status_3', EmailCampaignStatusHTMLSpanDraw.SCHEDULED);
      case EmailHistoryStatus.ERROR:
        return this.getHtmlRenderStatus('mail_send_status_5', EmailCampaignStatusHTMLSpanDraw.ERROR);
    }
  }

  private getHtmlRenderStatus(label, spanHTML) {
    const labelStatus = this.getTranslateLabels(label);
    return spanHTML + labelStatus + '</span>';
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  private getFormattedDateHtml(date) {
    const dateTime = formateDateTime(date);
    const splittedDateTime = dateTime.split(' ');
    const newDate = splittedDateTime[0];
    const time = splittedDateTime[2] ? splittedDateTime[1] + ' ' + splittedDateTime[2] : splittedDateTime[1];
    return '<span>' + newDate + '</span> - <span>' + time + '</span>';
  }

  private openPreviewModal(emailCampaignId) {
    this.emailHistoryService.getEmailPreview(emailCampaignId).subscribe(issue => {
      this.selectedEmailPreview = issue.htmlBody;
      this.modalEmailPreviewModal = this.modalService.open(this.modalEmailPreview, { size: 'xl' });

      this.modalEmailPreviewModal.result.then(
        result => {},
        reason => {}
      );
    });
  }

  private openConfirmDeleteModal(emailCampaignId) {
    this.modalConfirmDeleteModal = this.modalService.open(this.modalConfirmDelete);

    this.modalConfirmDeleteModal.result.then(
      result => {
        this.deleteCampaignIssue(emailCampaignId);
      },
      reason => {}
    );
  }

  deleteCampaignIssue(emailCampaignId) {
    this.emailHistoryService.deleteEmailCampaignIssue(emailCampaignId).subscribe(() => {
      this.grid.refresh();
      this.atlasToast.success('common_success', null);
    });
  }

  closeDelete() {
    this.modalConfirmDeleteModal.close();
  }
}
